import { useState, useRef, useCallback } from 'react';
import { Icon } from '../../icons';
import { getFile } from '../../api/files';
import { toast } from 'react-hot-toast';

export default function FileUpload({ label, accept = "image/*,.pdf", multiple = false, files = [], onChange, note, required = false }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const handleFiles = useCallback(fileList => {
    const arr = Array.from(fileList);
    onChange(multiple ? [...files, ...arr] : arr.slice(0, 1));
  }, [files, multiple, onChange]);
  const remove = useCallback(idx => onChange(files.filter((_, i) => i !== idx)), [files, onChange]);

  const handleView = async (f) => {
    try {
      if (f instanceof File) {
        window.open(URL.createObjectURL(f), '_blank');
        return;
      }
      const url = f.temporary_url || f.file_url;
      if (url) {
        window.open(url, '_blank');
        return;
      }
      if (f.id) {
        const res = await getFile(f.id);
        const newUrl = res.data?.data?.temporary_url || res.data?.data?.file_url || res.data?.temporary_url || res.data?.file_url;
        if (newUrl) {
          window.open(newUrl, '_blank');
        } else {
          toast.error("URL not available");
        }
      }
    } catch (err) {
      toast.error("Failed to open file");
      console.error(err);
    }
  };
  return (
    <div>
      {label && (
        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${drag ? "border-primary bg-primary-light dark:bg-primary/10" : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 hover:border-slate-300 dark:hover:border-slate-600"}`}>
        <div className="flex justify-center mb-2"><Icon name="upload" size={20} className="text-muted" /></div>
        <div className="text-sm font-semibold text-slate-500 dark:text-slate-400">Click to upload or drag & drop</div>
        <div className="text-xs text-muted mt-1">{note || "Supports JPG, PNG, PDF"}</div>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" onChange={e => handleFiles(e.target.files)} />
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 p-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-border dark:border-slate-700">
              <Icon name={f.type === "application/pdf" ? "documents" : "eye"} size={14} className="text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-ink dark:text-slate-200 truncate">{f.name || f.original_name}</div>
                <div className="text-sm text-muted">{(f.size / 1024).toFixed(1)} KB</div>
              </div>
              <div className="flex gap-3 items-center">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleView(f); }}
                  className="text-primary hover:text-primary/80 transition-colors bg-transparent border-0 cursor-pointer"
                >
                  <Icon name="eye" size={16} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  className="text-muted hover:text-red-500 transition-colors bg-transparent border-0 cursor-pointer"
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
